import { useCallback, useEffect, useState } from "react";
import { filterVisibleClasses, isRideTerminated } from "score-common";
import { useScoreApi, unwrap } from "../api/scoreApi";
// #3496 -- the badge COUNTS (Needs placings, Can't place yet) no longer come
// from here: the server counts them (GET /show/:showId/badge-counts, card
// #3463) and BadgeCountsContext serves them. toJudgedRide stays exported so a
// page that already holds a class's rides (the Classes page: its row chip count
// and the drawer's ride list) can run the shared score-common predicate over
// them for just the classes the server named.

export function toJudgedRide(r) {
  // #3457: horseName rides along so classNeedsPlacings can drop an FVD "No Ride"
  // filler row; it is not a ride and never reaches FINAL.
  return {
    status: r.derivedStatus ?? r.status ?? null,
    placing: r.placing ?? null,
    horseName: r.horseName,
    // #3464: the rider's division, the placing group key of a placed-by-division class.
    riderStatus: r.riderStatus ?? null,
    // #3462: what Can't place yet needs, and what its drawer list shows per ride.
    id: r.id,
    competitorNumber: r.competitorNumber ?? null,
    riderName: r.riderName ?? null,
    scheduledTime: r.scheduledTime ?? null,
    hc: r.hc === true,
    // #3505: a terminated ride (No Show, Excused, ...) takes no place.
    terminated: isRideTerminated(r),
  };
}

// #340/#862/#3496 -- the show's visible classes plus its rides grouped by class,
// each ride carrying its derived status. NOT a badge computation any more (see
// above): this stays for the things a page needs the rides for beyond a count
// (the test-disagreement tooltip, the placings-at-risk warnings, the drawer's
// stuck-ride list, the missing-signature banner). One showId-scoped GET /ride
// covers every class (mirrors the announcer's #637 running-order pattern,
// don't fan out per class), then one batched POST /rides/status resolves each
// ride's derived status. The name is historical.
//
// #2121 -- the class list and the show's rides run concurrently; only the
// status batch depends on the rides fetch.
//
// #2261 -- exposes the grouped `ridesByClassId` itself so ClassManager derives
// the ride-side test slugs from this ONE show-scoped fetch.
export function useClassesNeedingPlacings(showId) {
  const scoreApi = useScoreApi();
  const [classes, setClasses] = useState([]);
  const [ridesByClassId, setRidesByClassId] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refetch = useCallback(async () => {
    if (!showId) {
      setClasses([]);
      setRidesByClassId({});
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [classListRes, showRidesRes] = await Promise.all([
        scoreApi.classes.listClasses(showId),
        scoreApi.rides.listRides(undefined, showId),
      ]);
      // #1117 -- a hidden class never counts as needing placings.
      const classList = filterVisibleClasses(unwrap(classListRes));
      const showRides = unwrap(showRidesRes);
      const ids = [...new Set(showRides.map((r) => r.id).filter(Boolean))];
      const statusRows = ids.length
        ? unwrap(await scoreApi.rides.batchRideStatus({ rideIds: ids }))
        : [];
      const statusById = Object.fromEntries(statusRows.map((r) => [r.rideId, r.status]));
      const byClass = {};
      for (const r of showRides) {
        if (!r.classId) continue;
        (byClass[r.classId] ??= []).push({ ...r, derivedStatus: statusById[r.id] ?? null });
      }
      setClasses(classList);
      setRidesByClassId(byClass);
    } catch (err) {
      setClasses([]);
      setRidesByClassId({});
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [showId, scoreApi]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  // #3454: also exposes the (visible) `classes` it already fetched, so the
  // missing-signature banner reads the same one list instead of a second GET /class.
  return {
    classes,
    ridesByClassId,
    loading,
    error,
    refetch,
  };
}

export default useClassesNeedingPlacings;
